---
title: "Как удалить дубли строк из списка и не сломать порядок"
description: "Чистим списки email, артикулов, ID и ключевых слов: удаляем повторяющиеся строки локально в браузере."
locale: "ru"
date: "2026-06-26"
tags: ["дубли строк", "списки", "локально"]
relatedTools: ["dedupeLines"]
translations:
  en: "how-to-remove-duplicate-lines-from-a-list"
faq:
  - q: "Список отправляется на сервер?"
    a: "Нет. Удаление дублей выполняется в браузере, содержимое поля не уходит в сеть."
  - q: "Порядок строк сохранится?"
    a: "Да. Инструмент оставляет первое вхождение строки и удаляет последующие повторы."
  - q: "Пробелы и регистр учитываются?"
    a: "Да, по умолчанию совпадение точное. Опции позволяют игнорировать регистр или внешние пробельные символы Unicode только при сравнении, сохраняя первое вхождение без изменений."
---

Типичный рабочий сценарий: выгрузили клиентов из CRM, докинули ещё десять
email руками, потом склеили это с прошлой таблицей. Или собрали артикулы из
двух поставщиков и получили один и тот же SKU в разных местах. Проблема
всплывает поздно: импорт ругается, рассылка считает лишних людей, отчёт
показывает странное число строк.

В [удалении дублей строк](/udalit-dubli-strok) можно вставить список и оставить
только первое появление каждой строки. Для таких данных это важная деталь:
порядок часто уже что-то значит, особенно если список собирали вручную.

## Почему не всегда подходит таблица

Если это таблица с колонками, связями и формулами, лучше чистить её в Excel
или Google Sheets. Там есть фильтры, откат и понятная работа с колонками. Но
много списков живёт не в таблицах: textarea из админки, список ID из чата,
CSV-фрагмент без заголовков, небольшой фрагмент лога.

Загонять такой список в таблицу ради одной операции часто дольше, чем сама
чистка. Таблица может потерять ведущие нули в `00123`, превратить похожую на
дату строку в дату или странно показать длинный ID. Для сырого списка из
буфера обмена текстовый инструмент безопаснее.

## Точное совпадение или нормализация

Для кодов и ID начинайте с точного совпадения. `User-42`, `user-42` и
`user-42 ` выглядят почти одинаково, но в системе могут быть разными строками.
Автоматически "исправлять" такие значения опасно.

Для списков, которые собирали люди, наоборот, часто мешают пробельные символы.
Кто-то скопировал email с неразрывным пробелом в конце, кто-то вставил пустую
строку между группами. В таких случаях можно отдельно игнорировать внешние
пробельные символы Unicode при сравнении или удалить строки только из таких
символов. BOM остаётся значимым и никогда не удаляется как пробел. Первое
сохранённое значение при этом не обрезается и не меняет регистр. Эти правила
лучше включать осознанно, а не по умолчанию для всего подряд.

## Где это окупается

Ориентир простой:

| Можно чистить | Сначала подумать |
| --- | --- |
| email перед разовой рассылкой | журнал событий |
| артикулы перед импортом | аудит действий пользователя |
| промокоды перед загрузкой | логи ошибок без анализа причин |

Логи бывают разными. Если вы чистите шумный фрагмент, где повтор строки точно
не несёт смысла, дедупликация помогает. Если это журнал событий или аудит,
повтор может означать две одинаковые операции. Перед финальным импортом
сохраните исходник отдельно.
